import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Pencil, Plus, ShieldCheck, UserCog, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";
import type { Unit } from "./UnitsPage";

type User = {
  id: string;
  username_normalized: string;
  display_name: string;
  status: "active" | "locked" | "disabled";
  last_login_at: string | null;
  roles: string | null;
  version: number;
};
type Assignment = {
  id: string;
  role_id: string;
  role_code: string;
  role_name: string;
  status: string;
  starts_at: string;
  ends_at: string | null;
  scope_id: string;
  unit_id: string | null;
  scope_type: string;
  include_descendants: number;
  unit_name: string | null;
};
type UserDetails = User & { assignments: Assignment[] };
type Role = {
  id: string;
  code: string;
  name_ar: string;
  description_ar: string | null;
  is_system: number;
  status: "active" | "inactive";
  version: number;
  permission_count: number;
  permission_codes: string[];
};
type Permission = {
  id: string;
  code: string;
  resource: string;
  action: string;
  description_ar: string;
  sensitivity: string;
};

export function AdminPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [userCreateOpen, setUserCreateOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [roleCreateOpen, setRoleCreateOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const users = useQuery({ queryKey: ["users"], queryFn: async () => (await api<User[]>("/users")).data });
  const roles = useQuery({ queryKey: ["roles"], queryFn: async () => (await api<Role[]>("/roles")).data });
  const permissions = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => (await api<Permission[]>("/permissions")).data
  });
  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api<Unit[]>("/units/tree")).data
  });

  async function refreshUsers(message: string) {
    setNotice(message);
    await queryClient.invalidateQueries({ queryKey: ["users"] });
    if (selectedUserId) await queryClient.invalidateQueries({ queryKey: ["user", selectedUserId] });
  }
  async function refreshRoles(message: string) {
    setNotice(message);
    setSelectedRole(null);
    await queryClient.invalidateQueries({ queryKey: ["roles"] });
  }

  return (
    <div className="page">
      <PageHeader
        title="المستخدمون والأدوار والصلاحيات"
        description="حسابات متعددة الأدوار بنطاق عام أو جغرافي، مع حماية من فقدان صلاحيات الإدارة."
      />
      {notice ? (
        <div className="success-panel" role="status">
          <ShieldCheck aria-hidden="true" /> <p>{notice}</p>
        </div>
      ) : null}
      <section className="detail-grid admin-grid">
        <article className="content-card">
          <div className="section-head">
            <div>
              <h2>المستخدمون</h2>
              <p>الحسابات وحالاتها والأدوار الفعالة.</p>
            </div>
            {can("user.manage") ? (
              <button className="button" type="button" onClick={() => setUserCreateOpen(true)}>
                <Plus aria-hidden="true" /> مستخدم
              </button>
            ) : null}
          </div>
          {users.isPending ? (
            <LoadingState />
          ) : users.isError ? (
            <ErrorState error={users.error} />
          ) : (
            <div className="card-list compact">
              {users.data.map((user) => (
                <div className="list-row" key={user.id}>
                  <div>
                    <strong>{user.display_name}</strong>
                    <p>
                      {user.username_normalized} · {user.roles ?? "بلا دور"}
                    </p>
                  </div>
                  <div className="action-row">
                    <StatusBadge status={user.status} />
                    <button
                      className="button button--ghost"
                      type="button"
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <UserCog aria-hidden="true" /> إدارة
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
        <article className="content-card">
          <div className="section-head">
            <div>
              <h2>الأدوار</h2>
              <p>مجموعات الصلاحيات القابلة لإعادة الاستخدام.</p>
            </div>
            {can("role.manage") ? (
              <button className="button" type="button" onClick={() => setRoleCreateOpen(true)}>
                <Plus aria-hidden="true" /> دور
              </button>
            ) : null}
          </div>
          {roles.isPending ? (
            <LoadingState />
          ) : roles.isError ? (
            <ErrorState error={roles.error} />
          ) : (
            <div className="card-list compact">
              {roles.data.map((role) => (
                <div className="list-row" key={role.id}>
                  <div>
                    <strong>{role.name_ar}</strong>
                    <p>
                      {role.code} · {role.permission_count} صلاحية {role.is_system ? "· دور نظام" : ""}
                    </p>
                  </div>
                  <div className="action-row">
                    <StatusBadge status={role.status} />
                    {can("role.manage") ? (
                      <button
                        className="button button--ghost"
                        type="button"
                        onClick={() => setSelectedRole(role)}
                      >
                        <Pencil aria-hidden="true" /> تعديل
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>

      {userCreateOpen && roles.data && units.data ? (
        <UserCreatePanel
          roles={roles.data}
          units={units.data}
          onClose={() => setUserCreateOpen(false)}
          onSaved={() => refreshUsers("تم إنشاء المستخدم وتعيين دوره ونطاقه.")}
        />
      ) : null}
      {selectedUserId && roles.data && units.data ? (
        <UserManagementPanel
          userId={selectedUserId}
          roles={roles.data}
          units={units.data}
          canManage={can("user.manage")}
          onClose={() => setSelectedUserId(null)}
          onChanged={refreshUsers}
        />
      ) : null}
      {roleCreateOpen && permissions.data ? (
        <RoleCreatePanel
          permissions={permissions.data}
          onClose={() => setRoleCreateOpen(false)}
          onSaved={() => refreshRoles("تم إنشاء الدور وصلاحياته.")}
        />
      ) : null}
      {selectedRole && permissions.data ? (
        <RoleEditPanel
          role={selectedRole}
          permissions={permissions.data}
          onClose={() => setSelectedRole(null)}
          onSaved={() => refreshRoles("تم تحديث الدور والصلاحيات وتسجيل السبب.")}
        />
      ) : null}
    </div>
  );
}

function UserCreatePanel({
  roles,
  units,
  onClose,
  onSaved
}: {
  roles: Role[];
  units: Unit[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState(roles.find((role) => role.status === "active")?.id ?? "");
  const [unitId, setUnitId] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api("/users", {
        method: "POST",
        body: {
          username,
          displayName,
          password,
          status: "active",
          assignments: [{ roleId, scopes: [{ unitId: unitId || null, includeDescendants: true }] }]
        }
      }),
    onSuccess: async () => {
      await onSaved();
      onClose();
    }
  });
  return (
    <ManagementPanel title="إنشاء مستخدم" onClose={onClose}>
      <form
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="اسم المستخدم" htmlFor="new-username" hint="أحرف إنجليزية وأرقام ونقطة أو شرطة.">
          <input
            id="new-username"
            dir="ltr"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </Field>
        <Field label="الاسم الظاهر" htmlFor="new-display-name">
          <input
            id="new-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </Field>
        <Field label="كلمة المرور المؤقتة" htmlFor="new-password" hint="12 محرفًا على الأقل.">
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        <Field label="الدور الأول" htmlFor="new-role">
          <select id="new-role" value={roleId} onChange={(e) => setRoleId(e.target.value)} required>
            {roles
              .filter((role) => role.status === "active")
              .map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name_ar}
                </option>
              ))}
          </select>
        </Field>
        <Field label="نطاق الدور" htmlFor="new-user-scope" hint="النطاق العام يعني جميع الوحدات.">
          <select id="new-user-scope" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            <option value="">النظام كاملًا</option>
            {units
              .filter((unit) => unit.status === "active")
              .map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name_ar}
                </option>
              ))}
          </select>
        </Field>
        <MutationActions mutation={create} onCancel={onClose} label="إنشاء المستخدم" />
      </form>
    </ManagementPanel>
  );
}

function UserManagementPanel({
  userId,
  roles,
  units,
  canManage,
  onClose,
  onChanged
}: {
  userId: string;
  roles: Role[];
  units: Unit[];
  canManage: boolean;
  onClose: () => void;
  onChanged: (message: string) => Promise<void>;
}) {
  const details = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => (await api<UserDetails>(`/users/${userId}`)).data
  });
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [resetReason, setResetReason] = useState("");
  const [roleId, setRoleId] = useState(roles.find((role) => role.status === "active")?.id ?? "");
  const [unitId, setUnitId] = useState("");
  const update = useMutation({
    mutationFn: (status?: "active" | "disabled") =>
      api(`/users/${userId}`, {
        method: "PATCH",
        body: {
          displayName: displayName || details.data?.display_name,
          status,
          expectedVersion: details.data?.version
        }
      }),
    onSuccess: async () => {
      setDisplayName("");
      await onChanged("تم تحديث المستخدم وإدارة جلساته عند الحاجة.");
    }
  });
  const reset = useMutation({
    mutationFn: () =>
      api(`/users/${userId}/reset-password`, {
        method: "POST",
        body: { password, reason: resetReason, expectedVersion: details.data?.version }
      }),
    onSuccess: async () => {
      setPassword("");
      setResetReason("");
      await onChanged("تم تغيير كلمة المرور وإلغاء الجلسات السابقة.");
    }
  });
  const addAssignment = useMutation({
    mutationFn: () =>
      api(`/users/${userId}/role-assignments`, {
        method: "POST",
        body: { roleId, scopes: [{ unitId: unitId || null, includeDescendants: true }] }
      }),
    onSuccess: async () => onChanged("تمت إضافة الدور والنطاق للمستخدم.")
  });
  const revoke = useMutation({
    mutationFn: (assignmentId: string) =>
      api(`/users/${userId}/role-assignments/${assignmentId}`, { method: "DELETE" }),
    onSuccess: async () => onChanged("تم إلغاء تعيين الدور مع حفظ سجله التاريخي.")
  });

  if (details.isPending)
    return (
      <ManagementPanel title="إدارة المستخدم" onClose={onClose}>
        <LoadingState />
      </ManagementPanel>
    );
  if (details.isError)
    return (
      <ManagementPanel title="إدارة المستخدم" onClose={onClose}>
        <ErrorState error={details.error} />
      </ManagementPanel>
    );
  const user = details.data;
  const activeAssignments = groupAssignments(user.assignments.filter((item) => item.status === "active"));
  return (
    <ManagementPanel title={`إدارة ${user.display_name}`} onClose={onClose}>
      <div className="management-stack">
        <div className="row-between">
          <div>
            <strong>{user.username_normalized}</strong>
            <p>الإصدار {user.version}</p>
          </div>
          <StatusBadge status={user.status} />
        </div>
        {canManage ? (
          <form
            className="form-grid"
            onSubmit={(e) => {
              e.preventDefault();
              update.mutate(undefined);
            }}
          >
            <Field label="الاسم الظاهر" htmlFor="edit-display-name">
              <input
                id="edit-display-name"
                value={displayName}
                placeholder={user.display_name}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </Field>
            <div className="action-row field-actions">
              <button className="button" type="submit" disabled={update.isPending}>
                حفظ الاسم
              </button>
              <button
                className="button button--secondary"
                type="button"
                disabled={update.isPending}
                onClick={() => update.mutate(user.status === "active" ? "disabled" : "active")}
              >
                {user.status === "active" ? "تعطيل الحساب" : "تنشيط الحساب"}
              </button>
            </div>
          </form>
        ) : null}

        <section className="management-subsection">
          <h3>الأدوار والنطاقات</h3>
          {activeAssignments.length === 0 ? (
            <p>لا توجد أدوار فعالة.</p>
          ) : (
            activeAssignments.map((assignment) => (
              <div className="list-row" key={assignment.id}>
                <div>
                  <strong>{assignment.roleName}</strong>
                  <p>{assignment.scopes.join("، ")}</p>
                </div>
                {canManage ? (
                  <button
                    className="button button--ghost"
                    type="button"
                    onClick={() => revoke.mutate(assignment.id)}
                  >
                    إلغاء الدور
                  </button>
                ) : null}
              </div>
            ))
          )}
          {canManage ? (
            <form
              className="form-grid inset-form"
              onSubmit={(e) => {
                e.preventDefault();
                addAssignment.mutate();
              }}
            >
              <Field label="إضافة دور" htmlFor="assignment-role">
                <select id="assignment-role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                  {roles
                    .filter((role) => role.status === "active")
                    .map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name_ar}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="النطاق" htmlFor="assignment-scope">
                <select id="assignment-scope" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                  <option value="">النظام كاملًا</option>
                  {units
                    .filter((unit) => unit.status === "active")
                    .map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name_ar}
                      </option>
                    ))}
                </select>
              </Field>
              <button className="button form-span" type="submit" disabled={addAssignment.isPending}>
                إضافة الدور
              </button>
            </form>
          ) : null}
        </section>

        {canManage ? (
          <section className="management-subsection">
            <h3>إعادة تعيين كلمة المرور</h3>
            <form
              className="form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                reset.mutate();
              }}
            >
              <Field label="كلمة المرور الجديدة" htmlFor="reset-password">
                <input
                  id="reset-password"
                  type="password"
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>
              <Field label="سبب التغيير" htmlFor="reset-reason">
                <input
                  id="reset-reason"
                  minLength={5}
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  required
                />
              </Field>
              <button className="button form-span" type="submit" disabled={reset.isPending}>
                <KeyRound aria-hidden="true" /> تغيير وإلغاء الجلسات
              </button>
            </form>
          </section>
        ) : null}
        <CombinedErrors errors={[update.error, reset.error, addAssignment.error, revoke.error]} />
      </div>
    </ManagementPanel>
  );
}

function RoleCreatePanel({
  permissions,
  onClose,
  onSaved
}: {
  permissions: Permission[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: () =>
      api("/roles", {
        method: "POST",
        body: { code, nameAr: name, descriptionAr: description, permissionCodes: selected }
      }),
    onSuccess: async () => {
      await onSaved();
      onClose();
    }
  });
  return (
    <ManagementPanel title="إنشاء دور" onClose={onClose}>
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="رمز الدور" htmlFor="role-code">
          <input id="role-code" dir="ltr" value={code} onChange={(e) => setCode(e.target.value)} required />
        </Field>
        <Field label="اسم الدور" htmlFor="role-name">
          <input id="role-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="الوصف" htmlFor="role-description">
          <textarea
            id="role-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <PermissionPicker permissions={permissions} selected={selected} setSelected={setSelected} />
        <MutationActions mutation={create} onCancel={onClose} label="إنشاء الدور" />
      </form>
    </ManagementPanel>
  );
}

function RoleEditPanel({
  role,
  permissions,
  onClose,
  onSaved
}: {
  role: Role;
  permissions: Permission[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(role.name_ar);
  const [description, setDescription] = useState(role.description_ar ?? "");
  const [status, setStatus] = useState(role.status);
  const [selected, setSelected] = useState(role.permission_codes);
  const [reason, setReason] = useState("تحديث صلاحيات الدور من شاشة الإدارة");
  const save = useMutation({
    mutationFn: async () => {
      const updated = await api<{ version: number }>(`/roles/${role.id}`, {
        method: "PATCH",
        body: { nameAr: name, descriptionAr: description, status, expectedVersion: role.version }
      });
      return api(`/roles/${role.id}/permissions`, {
        method: "PUT",
        body: { permissionCodes: selected, reason, expectedVersion: updated.data.version }
      });
    },
    onSuccess: async () => {
      await onSaved();
      onClose();
    }
  });
  return (
    <ManagementPanel title={`تعديل ${role.name_ar}`} onClose={onClose}>
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="اسم الدور" htmlFor="edit-role-name">
          <input id="edit-role-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="الحالة" htmlFor="edit-role-status">
          <select
            id="edit-role-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as Role["status"])}
            disabled={role.code === "system-admin"}
          >
            <option value="active">نشط</option>
            <option value="inactive">معطل</option>
          </select>
        </Field>
        <Field label="الوصف" htmlFor="edit-role-description">
          <textarea
            id="edit-role-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field label="سبب تعديل الصلاحيات" htmlFor="role-reason">
          <input
            id="role-reason"
            minLength={5}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </Field>
        <PermissionPicker
          permissions={permissions}
          selected={selected}
          setSelected={setSelected}
          disabled={role.code === "system-admin"}
        />
        <MutationActions mutation={save} onCancel={onClose} label="حفظ الدور والصلاحيات" />
      </form>
    </ManagementPanel>
  );
}

function PermissionPicker({
  permissions,
  selected,
  setSelected,
  disabled = false
}: {
  permissions: Permission[];
  selected: string[];
  setSelected: (value: string[]) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="form-span checkbox-fieldset">
      <legend>الصلاحيات</legend>
      <div className="checkbox-grid permission-grid">
        {permissions.map((permission) => (
          <label className="check-option" key={permission.code}>
            <input
              type="checkbox"
              checked={selected.includes(permission.code)}
              disabled={disabled}
              onChange={(e) =>
                setSelected(
                  e.target.checked
                    ? [...selected, permission.code]
                    : selected.filter((code) => code !== permission.code)
                )
              }
            />
            <span>
              <strong>{permission.description_ar}</strong>
              <small dir="ltr">{permission.code}</small>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ManagementPanel({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <section className="content-card management-panel" aria-labelledby="management-panel-title">
      <div className="section-head">
        <h2 id="management-panel-title">{title}</h2>
        <button className="icon-button" type="button" onClick={onClose} aria-label="إغلاق">
          <X aria-hidden="true" />
        </button>
      </div>
      {children}
    </section>
  );
}

function MutationActions({
  mutation,
  onCancel,
  label
}: {
  mutation: { isPending: boolean; error: unknown };
  onCancel: () => void;
  label: string;
}) {
  return (
    <>
      <CombinedErrors errors={[mutation.error]} />
      <div className="dialog-actions form-span">
        <button className="button button--secondary" type="button" onClick={onCancel}>
          إلغاء
        </button>
        <button className="button" type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? "جارٍ الحفظ…" : label}
        </button>
      </div>
    </>
  );
}

function CombinedErrors({ errors }: { errors: unknown[] }) {
  const error = errors.find(Boolean);
  return error ? (
    <div className="inline-alert form-span" role="alert">
      {formatApiError(error)}
    </div>
  ) : null;
}

function groupAssignments(rows: Assignment[]): Array<{ id: string; roleName: string; scopes: string[] }> {
  const groups = new Map<string, { id: string; roleName: string; scopes: string[] }>();
  for (const row of rows) {
    const group = groups.get(row.id) ?? { id: row.id, roleName: row.role_name, scopes: [] };
    group.scopes.push(
      row.unit_name ? `${row.unit_name}${row.include_descendants ? " وفروعها" : ""}` : "النظام كاملًا"
    );
    groups.set(row.id, group);
  }
  return [...groups.values()];
}
